import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/SEOHead';

export default function PrivacyPolicyPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Privacy Policy | Technelle | Waukesha, WI"
        description="Read Technelle's privacy policy. Learn how we collect, use, and protect your personal information when you use our AI marketing and consulting services."
        keywords="Technelle privacy policy, data privacy Wisconsin, marketing agency privacy"
        canonical="https://technelle.ai/privacy-policy"
        ogTitle="Privacy Policy | Technelle"
        ogDescription="How Technelle collects, uses, and protects your personal information."
        ogUrl="https://technelle.ai/privacy-policy"
        robots="index, follow"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Privacy Policy', url: 'https://technelle.ai/privacy-policy' },
        ]}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
              alt="Technelle - AI Marketing & Consulting"
              className="h-14"
            />
          </Link>
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Services</Link>
            <Link to="/industries" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Industries</Link>
            <Link to="/resources" className="text-[15px] text-[#1a1a1a] hover:text-[#2196F3] transition-colors font-medium">Resources</Link>
          </div>
          <button
            onClick={() => setMenuOpen(true)}
            className="bg-[#2196F3] text-white px-6 sm:px-8 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap cursor-pointer min-w-[44px] min-h-[44px]"
            aria-label="Book free strategy call"
          >
            Book Free Strategy Call
          </button>
        </div>
      </nav>

      {/* Contact Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-start justify-end">
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
            <div className="p-8 sm:p-12">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1a1a1a]">Let's talk —</h2>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] hover:text-[#2196F3] transition-colors cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label="Close"
                >
                  <span className="text-[15px] font-medium">Close</span>
                </button>
              </div>
              <p className="text-[15px] text-gray-600">
                Reach out at{' '}
                <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a>
                {' '}or{' '}
                <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="pt-36 pb-16 bg-gray-50">
        <div className="container mx-auto px-8 max-w-4xl">
          <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">Legal</div>
          <h1 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-4 leading-tight">Privacy Policy</h1>
          <p className="text-[16px] text-gray-500">Last updated: April 2, 2026</p>
        </div>
      </section>

      {/* Content */}
      <main className="py-16">
        <div className="container mx-auto px-8 max-w-4xl">
          <div className="prose prose-lg max-w-none space-y-10 text-[16px] text-gray-700 leading-relaxed">

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">1. Introduction</h2>
              <p>
                Technelle ("we," "our," or "us"), based in Waukesha, Wisconsin, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <strong>technelle.ai</strong> or engage with our AI marketing and consulting services.
              </p>
              <p className="mt-3">
                Please read this policy carefully. If you disagree with its terms, please discontinue use of our site.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">2. Information We Collect</h2>
              <h3 className="text-[20px] font-semibold text-[#1a1a1a] mb-3">Information You Provide Directly</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and email address (via contact forms, newsletter sign-ups, or lead magnet downloads)</li>
                <li>Phone number and business information (when booking a strategy call)</li>
                <li>Any other information you voluntarily submit through our forms</li>
              </ul>
              <h3 className="text-[20px] font-semibold text-[#1a1a1a] mb-3 mt-6">Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address, browser type, and operating system</li>
                <li>Pages visited, time spent on pages, and referring URLs</li>
                <li>Device identifiers and cookie data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Respond to inquiries and schedule strategy calls</li>
                <li>Send you the free guide or resources you requested</li>
                <li>Send marketing communications (you may opt out at any time)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Analyze usage trends to enhance user experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">4. Sharing Your Information</h2>
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Service providers</strong> who assist in operating our website and conducting our business (e.g., email platforms, analytics tools), under strict confidentiality agreements</li>
                <li><strong>Legal authorities</strong> when required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">5. Cookies</h2>
              <p>
                Our website uses cookies to enhance your browsing experience. Cookies are small data files stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our site may not function properly without cookies.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">6. Data Retention</h2>
              <p>
                We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, or as required by law. When your data is no longer needed, we securely delete or anonymize it.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your personal data</li>
                <li>Opt out of marketing communications at any time</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, contact us at{' '}
                <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a>.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">8. Security</h2>
              <p>
                We implement reasonable administrative, technical, and physical security measures to protect your personal information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last updated" date at the top of this page. Your continued use of our website after any changes constitutes your acceptance of the new policy.
              </p>
            </section>

            <section>
              <h2 className="text-[28px] font-bold text-[#1a1a1a] mb-4">11. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <div className="mt-4 bg-gray-50 rounded-xl p-6 space-y-2">
                <p><strong>Technelle</strong></p>
                <p>Waukesha, Wisconsin</p>
                <p>
                  Email:{' '}
                  <a href="mailto:melissa@technelle.ai" className="text-[#2196F3] hover:underline">melissa@technelle.ai</a>
                </p>
                <p>
                  Phone:{' '}
                  <a href="tel:4145309114" className="text-[#2196F3] hover:underline">414-530-9114</a>
                </p>
              </div>
            </section>

          </div>

          <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/terms" className="text-[#2196F3] hover:underline text-[15px] font-medium">
              View Terms &amp; Conditions
            </Link>
            <span className="hidden sm:block text-gray-300">|</span>
            <Link to="/" className="text-gray-600 hover:text-[#2196F3] transition-colors text-[15px]">
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-12 mt-16">
        <div className="container mx-auto px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-[14px] text-gray-400">Technelle™ &nbsp;·&nbsp; Waukesha, Wisconsin</div>
            <div className="flex items-center gap-6 text-[14px]">
              <Link to="/privacy-policy" className="text-[#2196F3] font-medium">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms</Link>
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
